import { debounce } from 'lodash'
import { Suspense, useCallback, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Button } from 'src/shared/components/ui/buttons/button'
import { useWindowSize } from 'src/shared/hooks/use-window-size'

import { usePublicUsersBySearch } from '../../api/marketplace/use-public-search'
import useActiveFilterStore from '../../stores/marketplace/active-filter'
import { formatCity, formatState } from '../../utils/format-location'

import HomeAffordCard from './home-afford-card'
import PublicUserCard from './public-user-card'
import PublicUserCardSkeleton from './public-user-card-skeleton'
import PublicUserSeparator from './public-user-separator'

const EmptyUserList = () => {
  const { width } = useWindowSize()

  const onClick = () => {
    // TODO: Implement the logic to find someone and then reach back out to the user
  }

  let skeletonsAmount = 5
  if (width >= 1880) {
    skeletonsAmount = 6
  } else if (width >= 1550) {
    skeletonsAmount = 5
  } else if (width >= 1280) {
    skeletonsAmount = 4
  } else if (width >= 1024) {
    skeletonsAmount = 3
  } else if (width >= 768) {
    skeletonsAmount = 2
  } else {
    skeletonsAmount = 5
  }

  return (
    <div className="relative grid grid-cols-1 gap-6 overflow-y-auto pb-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1880px]:grid-cols-6">
      {Array.from({ length: skeletonsAmount }).map((_, index) => (
        <PublicUserCardSkeleton key={index} />
      ))}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex w-[300px] flex-col items-center justify-center gap-4 rounded-xl bg-primary p-6 shadow-xl">
          <p className="text-center text-white">
            No one&apos;s available right now, would you like us to find someone &amp; then reach back out to you?
          </p>
          <Button onClick={onClick} className="w-full bg-white text-primary">
            Yes!
          </Button>
        </div>
      </div>
    </div>
  )
}

const PublicUserListContent = () => {
  const submittedFilters = useActiveFilterStore(useShallow((state) => state.submittedFilters))

  // Format the city and state from submitted filters
  const formattedCity = useMemo(() => formatCity(submittedFilters.city || ''), [submittedFilters.city])
  const formattedState = useMemo(() => formatState(submittedFilters.state || ''), [submittedFilters.state])

  const {
    data: searchUsers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePublicUsersBySearch(
    25,
    '',
    submittedFilters.profession || undefined,
    submittedFilters.longitude || undefined,
    submittedFilters.latitude || undefined,
    submittedFilters.expertise || [],
    submittedFilters.clientFocus || undefined,
    submittedFilters.city || undefined,
    submittedFilters.state || undefined,
  )

  // Fix the debounced load more function
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Create a memoized debounced version that won't recreate on every render
  const debouncedLoadMore = useMemo(() => debounce(loadMore, 100), [loadMore])

  const observer = useRef<IntersectionObserver | null>(null)
  const lastCardRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          debouncedLoadMore()
        }
      })
      if (node) observer.current.observe(node)
    },
    [debouncedLoadMore],
  )

  const searchResults = useMemo(() => {
    if (!searchUsers?.pages || searchUsers.pages.length === 0)
      return { results: [], withinCityStateCutoff: 0, nearbyCutoff: 0, totalCount: 0 }

    const flatResults = searchUsers.pages.flatMap((page) => page.results)
    const firstPage = searchUsers.pages[0]

    return {
      results: flatResults,
      withinCityStateCutoff: firstPage.withinCityStateCutoff,
      nearbyCutoff: firstPage.nearbyCutoff,
      totalCount: firstPage.totalCount,
    }
  }, [searchUsers])

  const { results, withinCityStateCutoff, nearbyCutoff } = searchResults

  const hasWithinResults = withinCityStateCutoff > 0
  const hasNearbyResults = nearbyCutoff > withinCityStateCutoff
  const hasStateResults = results.length > nearbyCutoff

  // Flatten posts data for virtualization
  const userCards = useMemo(() => {
    if (!results || results.length === 0) return []

    const cards: React.ReactNode[] = []

    // Add city/state results and separator
    if (hasWithinResults) {
      const cityResults = results.slice(0, withinCityStateCutoff)
      cityResults.forEach((user, index) => {
        const isLast = index === cityResults.length - 1
        cards.push(
          <div ref={isLast ? lastCardRef : undefined} key={user.id} className="h-full">
            <PublicUserCard user={user} isWithin={true} />
          </div>,
        )
      })

      if (hasNearbyResults || hasStateResults) {
        cards.push(
          <PublicUserSeparator
            key="city-separator"
            city={formattedCity}
            state={formattedState}
            isEmptyArea={false}
            isEndOfAllResults={!hasNearbyResults}
          />,
        )
      }
    } else if (!hasWithinResults && hasNearbyResults) {
      cards.push(
        <PublicUserSeparator key="nearby-separator" city={formattedCity} state={formattedState} isEmptyArea={true} />,
      )
    } else if (!hasWithinResults && !hasNearbyResults && hasStateResults && submittedFilters.state) {
      cards.push(
        <PublicUserSeparator
          key="city-separator-state"
          city={formattedCity}
          state={formattedState}
          isEmptyArea={true}
          isEndOfAllResults={true}
        />,
      )
    }

    // Add nearby results and separator
    if (hasNearbyResults) {
      const nearbyResults = results.slice(withinCityStateCutoff, nearbyCutoff)
      nearbyResults.forEach((user, index) => {
        const isLast = !hasStateResults && index === nearbyResults.length - 1
        cards.push(
          <div ref={isLast ? lastCardRef : undefined} key={user.id} className="h-full">
            <PublicUserCard user={user} isWithin={false} isNearby={true} />
          </div>,
        )
      })

      if (hasStateResults) {
        cards.push(
          <PublicUserSeparator
            key="nearby-separator-state"
            city={formattedCity}
            state={formattedState}
            isEmptyArea={false}
            isEndOfAllResults={true}
          />,
        )
      }
    }

    // Add state-licensed results
    if (hasStateResults) {
      const stateResults = results.slice(nearbyCutoff)
      stateResults.forEach((user, index) => {
        const isLast = index === stateResults.length - 1
        cards.push(
          <div ref={isLast ? lastCardRef : undefined} key={user.id} className="h-full">
            <PublicUserCard user={user} isWithin={false} isNearby={false} />
          </div>,
        )
      })
    }

    if (cards.length === 0) {
      cards.push(
        <PublicUserSeparator key="empty-separator" city={formattedCity} state={formattedState} isEmptyArea={true} />,
      )
    }

    // Add home afford card as the 7th card.
    if (cards.length >= 6) {
      cards.splice(6, 0, <HomeAffordCard />)
    }

    return cards
  }, [
    results,
    withinCityStateCutoff,
    nearbyCutoff,
    formattedCity,
    formattedState,
    lastCardRef,
    hasWithinResults,
    hasNearbyResults,
    hasStateResults,
    submittedFilters.state,
  ])

  return (
    <div className="mb-[120px]">
      {userCards.length === 0 ? (
        <EmptyUserList />
      ) : (
        <div className="grid grid-cols-1 gap-3 overflow-y-auto pb-8 sm:grid-cols-1 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1880px]:grid-cols-6">
          {userCards}
          {isFetchingNextPage && (
            <>
              <PublicUserCardSkeleton />
              <PublicUserCardSkeleton />
              <PublicUserCardSkeleton />
              <PublicUserCardSkeleton />
            </>
          )}
        </div>
      )}
    </div>
  )
}

const PublicUserListSkeleton = ({ skeletonsAmount = 25 }: { skeletonsAmount?: number }) => {
  return (
    <div className="relative grid grid-cols-1 gap-6 overflow-y-auto pb-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1880px]:grid-cols-6">
      {Array.from({ length: skeletonsAmount }).map((_, index) => (
        <PublicUserCardSkeleton key={index} />
      ))}
    </div>
  )
}

const PublicUserList = () => {
  const { width } = useWindowSize()

  let skeletonsAmount = 5
  if (width >= 1880) {
    skeletonsAmount = 6
  } else if (width >= 1550) {
    skeletonsAmount = 5
  } else if (width >= 1280) {
    skeletonsAmount = 4
  } else if (width >= 1024) {
    skeletonsAmount = 3
  } else if (width >= 768) {
    skeletonsAmount = 2
  } else {
    skeletonsAmount = 5
  }

  return (
    <Suspense fallback={<PublicUserListSkeleton skeletonsAmount={skeletonsAmount} />}>
      <PublicUserListContent />
    </Suspense>
  )
}

export default PublicUserList
